import React from 'react';
import { AppLayout } from '../components/AppLayout';
import LandingHeader from '../components/LandingHeader';
import Footer from '../../components/Footer';
import { isIOSDevice } from '../utils/deviceDetection';
import { useAuth } from '../components/AuthProviderWrapper';
import { BackButton } from '../components/BackButton';

const ContactPage: React.FC = () => {
  const isIOS = isIOSDevice();
  const { isAuthenticated } = useAuth();
  
  const content = (
    <div className="max-w-4xl mx-auto px-4" style={{ 
      paddingTop: isAuthenticated && isIOS ? '5rem' : '2rem', 
      paddingBottom: '2rem' 
    }}>
      <div className="mb-4">
        <BackButton />
      </div>
      <h1 className="text-3xl font-bold mb-6 text-gray-800">Contact Us</h1>
      
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Get in Touch</h2>
        <p className="text-gray-600 mb-6">
          We&apos;d love to hear from you! Whether you have a question about features, need technical support, 
          or want to share feedback, our team is ready to help.
        </p>
        
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold mb-2">Email Support</h3>
            <p className="text-gray-600">
              For general inquiries and support: <a href="mailto:contact-us@dinner-brain.com" className="text-[#6E9C87] hover:underline">contact-us@dinner-brain.com</a>
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2">Response Time</h3>
            <p className="text-gray-600">We typically respond within 24-48 hours during business days.</p>
          </div>
          
          <div>
            <h3 className="font-semibold mb-2">Common Topics</h3>
            <ul className="list-disc pl-5 text-gray-600 space-y-1">
              <li>Account and subscription questions</li>
              <li>Technical support and troubleshooting</li>
              <li>Feature requests and suggestions</li>
              <li>Bug reports</li>
              <li>Recipe sharing and collaboration</li>
              <li>Mobile app issues</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-800">How do I reset my password?</h3>
            <p className="text-gray-600 mt-1">
              Click the &ldquo;Forgot Password&rdquo; link on the login page and follow the instructions sent to your email.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-800">Can I export my recipes?</h3>
            <p className="text-gray-600 mt-1">
              Yes! You can export your recipes from the Import/Export page in various formats including JSON and CSV.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-800">How do I share recipes with friends?</h3>
            <p className="text-gray-600 mt-1">
              Open any recipe and click the &ldquo;Share&rdquo; button to get a shareable link that works even for people without accounts.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-800">Is my data secure?</h3>
            <p className="text-gray-600 mt-1">
              Absolutely. We use industry-standard encryption and security measures to protect your data. 
              Your recipes and personal information are stored securely and never shared without your permission.
            </p>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-800">Can I use DinnerBrain offline?</h3>
            <p className="text-gray-600 mt-1">
              The app works best with an internet connection, but we cache your recently viewed recipes for offline access.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // If logged in, use AppLayout with regular header
  if (isAuthenticated) {
    return (
      <AppLayout>
        {content}
      </AppLayout>
    );
  }

  // If logged out, use LandingHeader
  return (
    <div className="min-h-screen bg-white">
      <LandingHeader />
      <div style={{ 
        paddingTop: isIOS ? 'calc(5rem + env(safe-area-inset-top, 0px))' : '5rem'
      }}>
        {content}
      </div>
      <Footer />
    </div>
  );
};

export default ContactPage;